import type { GeometryPayload, GeometryShapePart } from '../types/mathGeometry';

export interface GeometryBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function partBounds(part: GeometryShapePart): GeometryBounds {
  const ox = part.offsetX ?? 0;
  const oy = part.offsetY ?? 0;
  const d = part.dimensions;
  if (part.shapeType === 'circle') {
    const r = d.radius ?? 1;
    return { minX: ox - r, minY: oy - r, maxX: ox + r, maxY: oy + r };
  }
  if (part.shapeType === 'triangle') {
    const w = d.base ?? d.width ?? 1;
    const h = d.height ?? 1;
    return { minX: ox, minY: oy, maxX: ox + w, maxY: oy + h };
  }
  const w = d.width ?? 1;
  const h = d.height ?? 1;
  return { minX: ox, minY: oy, maxX: ox + w, maxY: oy + h };
}

export function geometryWorldBounds(geometry: GeometryPayload): GeometryBounds {
  if (geometry.shapeType === 'compound' && geometry.parts?.length) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const part of geometry.parts) {
      const b = partBounds(part);
      minX = Math.min(minX, b.minX);
      minY = Math.min(minY, b.minY);
      maxX = Math.max(maxX, b.maxX);
      maxY = Math.max(maxY, b.maxY);
    }
    return { minX, minY, maxX, maxY };
  }

  const d = geometry.dimensions;
  if (geometry.shapeType === 'circle') {
    const r = d.radius ?? 1;
    return { minX: -r, minY: -r, maxX: r, maxY: r };
  }
  if (geometry.shapeType === 'triangle') {
    const w = d.base ?? d.width ?? 1;
    const h = d.height ?? 1;
    return { minX: 0, minY: 0, maxX: w, maxY: h };
  }
  if (geometry.shapeType === 'regular_polygon') {
    const sides = Math.max(3, Math.round(Number(d.sides) || 5));
    const sideLength = Number(d.sideLength) || 2;
    const radius = sideLength / (2 * Math.sin(Math.PI / sides));
    return { minX: -radius, minY: -radius, maxX: radius, maxY: radius };
  }
  const w = d.width ?? 1;
  const h = d.height ?? 1;
  return { minX: 0, minY: 0, maxX: w, maxY: h };
}

export function geometryLayout(
  geometry: GeometryPayload,
  canvasW: number,
  canvasH: number,
  labelPad = 36,
) {
  const bounds = geometryWorldBounds(geometry);
  const worldW = Math.max(0.01, bounds.maxX - bounds.minX);
  const worldH = Math.max(0.01, bounds.maxY - bounds.minY);
  const innerW = canvasW - labelPad * 2;
  const innerH = canvasH - labelPad * 2;
  const scale = Math.min(innerW / worldW, innerH / worldH);
  const drawW = worldW * scale;
  const drawH = worldH * scale;
  const originX = labelPad + (innerW - drawW) / 2 - bounds.minX * scale;
  const originY = labelPad + (innerH - drawH) / 2 - bounds.minY * scale;
  return { scale, originX, originY, bounds, worldW, worldH };
}

export function worldToSvg(x: number, y: number, layout: ReturnType<typeof geometryLayout>) {
  return {
    x: layout.originX + x * layout.scale,
    y: layout.originY + y * layout.scale,
  };
}

const DEFAULT_MAX_WIDTH = 448;
const MIN_CANVAS_HEIGHT = 120;
const MAX_CANVAS_HEIGHT = 360;

/** Deriva tamaño del lienzo a partir del aspect ratio real de las dimensiones. */
export function resolveGeometryCanvasSize(
  geometry: GeometryPayload,
  maxWidth = DEFAULT_MAX_WIDTH,
  labelPad = 36,
) {
  const bounds = geometryWorldBounds(geometry);
  const worldW = Math.max(0.01, bounds.maxX - bounds.minX);
  const worldH = Math.max(0.01, bounds.maxY - bounds.minY);
  const aspect = worldW / worldH;

  let canvasW = maxWidth;
  let canvasH = Math.round((canvasW - labelPad * 2) / aspect + labelPad * 2);

  if (canvasH > MAX_CANVAS_HEIGHT) {
    canvasH = MAX_CANVAS_HEIGHT;
    canvasW = Math.round((canvasH - labelPad * 2) * aspect + labelPad * 2);
  }

  canvasH = Math.max(canvasH, MIN_CANVAS_HEIGHT);
  return { canvasW, canvasH, aspect };
}
